import { useParams } from 'react-router'

import { useFvNavigate } from '@fv/client-components'

import { Loading } from '@/components/Loading'
import { LoadSummary } from '@/components/LoadSummary/LoadSummary'
import { useLoad } from '@/hooks/shipments'
import { useLoadRatesPage } from '@/route-hooks'
import { routes } from '@/routes'

import { useAwardLoads } from '../booking/mutations'
import { useUpdateShipment } from '../shipment-form/mutations'
import { buildShipmentLocation } from '../shipment-location-form/types'
import { buildAwardLoadsPayload } from './awardLoadMappers'
import { ShipmentForm } from './ShipmentForm'
import { ShipmentFormPageLayout } from './ShipmentFormPageLayout'
import { ShipmentFormProvider } from './ShipmentFormProvider'
import { useShipmentFormState } from './store'
import type { ShipmentFormModel } from './types'
import { mapShipmentFormModelToLoadDTO } from './utils'

const useLoadId = () => {
  const { loadId } = useParams()
  return loadId
}

export const ShipmentEditPage = () => {
  const loadId = useLoadId()
  const loadQuery = useLoad(loadId)
  const isLoading = loadQuery.isLoading
  const load = loadQuery.data

  if (isLoading) return <Loading />

  return (
    <ShipmentFormProvider init={{ load }}>
      <ShipmentEditPageContent />
    </ShipmentFormProvider>
  )
}

const ShipmentEditPageContent = () => {
  const award = useAwardLoads()
  const { locations, initialLoad: load } = useShipmentFormState()
  const update = useUpdateShipment(load.loadId)
  const navigate = useFvNavigate()
  const ratesPage = useLoadRatesPage()

  const handleSave = async (model: ShipmentFormModel) => {
    if (load.status === 'pending') {
      await update.mutateAsync(
        mapShipmentFormModelToLoadDTO(model, load.workflow),
      )
      return navigate(ratesPage(load))
    }

    const payload = buildAwardLoadsPayload(
      model,
      load.loadId,
      load.selectedQuote._id,
      true,
    )

    // A failed pickup request is rebooked; anything else is already booked and just edited.
    await award.mutateAsync(
      load.pickup?.status === 'error'
        ? {
            ...payload,
            schedulePickup: load.schedulePickup ?? true,
            bolNumber: model.bolNum,
            tags: [],
          }
        : { isEdit: true, ...payload, bolNumber: model.bolNum },
    )
    return navigate(routes.details(load.loadId))
  }
  return (
    <ShipmentFormPageLayout
      sidebarContent={
        <div className="flex-1 h-full flex">
          <LoadSummary
            hideEdit
            load={{
              ...load,
              locations: locations.map(buildShipmentLocation),
            }}
          />
        </div>
      }
    >
      <ShipmentForm
        isSaving={award.isPending || update.isPending}
        canSkipItems={
          load.workflow === 'truckload' && load.status === 'pending'
        }
        onSave={handleSave}
      />
    </ShipmentFormPageLayout>
  )
}
